import { Box, BoxProps, Button, Grid, styled, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ClientSettingName, IArrivalMessagePayload, IClientSettings } from "@vmosolutions/oc-interfaces";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomFieldsV2 } from "src/components/Custom/CustomFieldsV2";
import {
  FormikDateTimeField,
  FormikSelectField,
  FormikTextField,
} from "src/components/FormikComponents/FormikWrappers";
import { getClientSetting } from "src/redux/actions/App.Actions";
import { postArrivalOrDepartureMessage } from "src/redux/actions/flights/FlightData.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { formatDelayCode } from "src/utils/delayCode";
import { IDelayOptions } from "../FlightEditor/DelaysForm/DelaysForm";

export interface IArrivalMessages {
  flightId: string;
  customFields?: any;
  windowId: string;
  delayOptions: IDelayOptions[];
  destination: string;
  actualOut: number;
  actualOff: number;
  actualOn: number;
  actualIn: number;
  setRefresh: (refresh: boolean) => void;
}

interface IArrivalMessageValues {
  delays: IDelayOptions[];
  arrivalMessage: {
    actualOff: number;
    actualOn: number;
    actualIn: number;
    notes: string;
    custom: object;
  };
}
export type ArrivalMessageForm = FormikProps<IArrivalMessageValues>;

const defaultValues: IArrivalMessageValues = {
  delays: [],
  arrivalMessage: {
    actualOff: undefined,
    actualOn: undefined,
    actualIn: undefined,
    notes: undefined,
    custom: {},
  },
};

const validateTimes = (values: IArrivalMessageValues): any => {
  if (!values?.arrivalMessage) return {};
  const { actualOff, actualOn, actualIn } = values.arrivalMessage;
  // nested values have to be added here as empty objects.
  const errors: any = {
    arrivalMessage: {},
  };

  if (!actualOn) {
    errors.arrivalMessage.actualOn = "Requires actual arrival Date/time.";
  }
  if (!actualIn) {
    errors.arrivalMessage.actualIn = "Requires actual enter gate date/time.";
  }

  // Check if actual arrival is after departure.
  if (!!actualOff && !!actualOn) {
    if (new Date(actualOn) <= new Date(actualOff)) {
      errors.arrivalMessage.actualOn = "Flight must arrive after its departure.";
    }
  }

  if (!!actualOn && !!actualIn) {
    if (new Date(actualIn) < new Date(actualOn)) {
      errors.arrivalMessage.actualOn = "Flight cannot enter gate before it lands.";
      errors.arrivalMessage.actualIn = "Flight cannot enter gate before it lands.";
    }
  }

  return Object.keys(errors?.arrivalMessage)?.length ? errors : {};
};

const ArrivalMessagesWrapper: React.FC<BoxProps> = styled(Box)(({ theme }) => ({
  minWidth: 363,
  "& .form_footer": {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-start",
    "& button": { margin: "5px 5px" },
    "& .btn": {
      flexWrap: "wrap",
      display: "flex",
      height: 27,
      fontSize: 10,
      lineHeight: "12px",
      padding: "2px 8px",
      margin: "0px 4px",
      "&.submit": {
        color: theme.custom.forms.inputBack,
      },
    },
  },
  "& .MuiFormLabel-root": {
    minWidth: 150,
  },

  "& p.required_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
    width: "100%",
  },
  "& .required_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
    padding: 10,
  },
}));

/**
 * Movement Message - 'Arrival Messages' form.
 * @param props -
 */
export const ArrivalMessages = (props: IArrivalMessages): JSX.Element => {
  const {
    // destination,
    flightId,
    delayOptions,
    windowId,
    actualOff,
    actualOn,
    actualIn,
    customFields,
    setRefresh,
  } = props;

  const dispatch = useDispatch();
  const customSchema = useSelector((state: IRootState) => state.customPages?.movementMessages?.arrival);
  const delayCodeFormat = useSelector((state: IRootState) => state.UISettings.delayCodeFormat);
  const schema = useMemo(() => (customSchema ? JSON.parse(customSchema) : null), [customSchema]);
  const [delaySetting, setDelaySetting] = useState<IClientSettings | null>();
  const [delayLoaded, setDelayLoaded] = useState<boolean>(false);

  useEffect(() => {
    const getDisplaySetting = async (): Promise<void> => {
      console.log(ClientSettingName);
      const displaySetting = await getClientSetting(ClientSettingName.DelaySettings);
      setDelaySetting(displaySetting?.setValue?.enabled ? displaySetting : null);
      setDelayLoaded(true);
    };
    getDisplaySetting();
  }, []);

  // Initial values for Arrival Message
  const initialValues = useMemo(() => {
    const initialCustom: { [key: string]: any } = {};
    if (schema?.properties) {
      Object.keys(schema.properties).forEach((value: string) => {
        // If available, set custom value passed in through object, otherwise use default in schema
        initialCustom[value] =
          customFields?.[value] ??
          schema.properties[value].default ??
          schema.properties[value]?.selectorProps?.defaultValue;
      });
    }
    return {
      ...defaultValues,
      arrivalMessage: {
        ...defaultValues.arrivalMessage,
        actualOff, // Only passed into the form to validate times. (not passed into payload)
        actualOn,
        actualIn,
        custom: initialCustom,
      },
    };
  }, [actualOff, actualOn, actualIn, customFields, schema]);

  const mosaicPane = useMemo(() => document?.getElementById(windowId + "-panel") ?? null, [windowId]);

  const handleSubmit = async (
    values: IArrivalMessageValues,
    formikHelpers: FormikHelpers<IArrivalMessageValues>
  ): Promise<void> => {
    const { setStatus, setSubmitting, resetForm } = formikHelpers;
    setSubmitting(true);
    setStatus("");

    // Remap delay codes to smaller format.
    const selectedDelayCodes: { codeId: string; onDeparture: boolean }[] = [];
    values?.delays?.forEach((opt: IDelayOptions) => {
      selectedDelayCodes.push({ onDeparture: false, codeId: opt._id });
    });

    const arrivalMessage = { ...values.arrivalMessage };
    delete arrivalMessage.actualOff;

    const payload: IArrivalMessagePayload = {
      delays: selectedDelayCodes,
      arrivalMessage,
    };

    const response: any = await dispatch(postArrivalOrDepartureMessage(flightId, payload));
    // if successful - post message for user and lock form
    if (response?.success) {
      // setStatus("Arrival Message sent Successfully, reset tool to send another.");
      // Set status above no longer needed as setRefresh automatically refreshes tool.
      setRefresh(true);
      // Reset selected delays and notes fields.
      const resetArrMessages: any = { ...values.arrivalMessage, notes: undefined };
      resetForm({ values: { delays: [], arrivalMessage: resetArrMessages } });
    } else {
      setStatus("Unable to submit Arrival Message, please see administrator for assistance.");
    }
    setSubmitting(false);
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={handleSubmit} validate={validateTimes}>
      {(arrivalMessageForm: ArrivalMessageForm) => {
        const { status, dirty, isValid, isSubmitting, handleReset } = arrivalMessageForm;
        return (
          <Form>
            <ArrivalMessagesWrapper>
              <Grid container className="form_body">
                <p className="required_fields_label">required fields</p>

                <Grid container>
                  <Grid item xs={12}>
                    <FormikDateTimeField
                      className="split_label_input"
                      name="arrivalMessage.actualOn"
                      label="On Time (UTC)"
                      //displayOnlyTime={true}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormikDateTimeField
                      className="split_label_input"
                      name="arrivalMessage.actualIn"
                      label="In Time (UTC)"
                      //displayOnlyTime={true}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <FormikSelectField
                    className="delay_codes"
                    name="delays"
                    label="Delay Codes"
                    options={delayOptions ?? []}
                    getOptionLabel={(delay: IDelayOptions) => formatDelayCode(delay, delayCodeFormat)}
                    getOptionValue={(delay: IDelayOptions) => delay.delayCode._id}
                    isMulti={true}
                    isSearchable={true}
                    isClearable={true}
                    menuPosition="fixed"
                    maxMenuHeight={400}
                    menuPortalTarget={mosaicPane}
                    openMenuOnFocus={false}
                    placeholder="Select Delay Codes"
                    filterOptions={{
                      ignoreCase: true,
                      ignoreAccents: true,
                      matchFrom: "any" as const,
                      stringify: (option: any) => `${option.label}`,
                      trim: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormikTextField
                    className="textfield"
                    label={
                      !delayLoaded ? "" : (delaySetting?.setValue?.movementMessageForm?.noteLabel ?? "Arrival Note")
                    }
                    name="arrivalMessage.notes"
                    placeholder=""
                    multiline={true}
                    minRows={3}
                    maxRows={6}
                  />
                </Grid>

                <Grid xs={12}>
                  <CustomFieldsV2 schema={schema} objectPath="arrivalMessage.custom" />
                </Grid>

                <Typography variant="caption" color="textPrimary" style={{ margin: "0px 10px" }}>
                  {status}
                </Typography>

                <Grid container className="form_footer">
                  <Button
                    className="btn reset"
                    type="reset"
                    variant="contained"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button
                    className={"btn save"}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !isValid}
                    type="submit"
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </ArrivalMessagesWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
